import React from 'react'
import { Chip, Menu, MenuItem } from '@mui/material'
import { useHistory } from 'react-router-dom'
import { useGetBusinessID } from '@marketing-milk/frontend'
import { format } from 'date-fns'
import { CreativeImportJobStatus, CreativeImportListItem } from '@marketing-milk/interfaces'
import { formatTimeRemaining } from './stagingCountdown'
import { useCreativeImportRecent } from './useCreativeImportRecent'

// A typed Record over the enum is exhaustive at compile time, so a new status
// can't quietly render blank. Same shape as ROW_STATUS_CHIP in ImportStep.
const JOB_STATUS_CHIP: Record<
  CreativeImportJobStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  [CreativeImportJobStatus.AwaitingConfirmation]: { label: 'Not confirmed', color: 'default' },
  [CreativeImportJobStatus.Queued]: { label: 'Queued', color: 'info' },
  [CreativeImportJobStatus.Processing]: { label: 'Importing', color: 'info' },
  [CreativeImportJobStatus.Completed]: { label: 'Done', color: 'success' },
  [CreativeImportJobStatus.CompletedWithErrors]: { label: 'Done with errors', color: 'warning' },
  [CreativeImportJobStatus.Failed]: { label: 'Failed', color: 'error' },
  [CreativeImportJobStatus.Interrupted]: { label: 'Interrupted', color: 'warning' },
}

function expiryLabel(stagingExpiresAt: string, now: number): string {
  const remaining = new Date(stagingExpiresAt).getTime() - now
  return remaining <= 0 ? 'Expired' : `Expires in ${formatTimeRemaining(remaining)}`
}

type Props = {
  /** The control that opened it — the import button's caret. Null when closed. */
  anchorEl: HTMLElement | null
  onClose: () => void
}

/**
 * The last handful of imports, each linking to the URL it already has. Answers
 * "get me back into the one I just ran" — not a browsable history, so no counts
 * and no page of its own.
 */
export const RecentImportsMenu = ({ anchorEl, onClose }: Props) => {
  const history = useHistory()
  const businessID = useGetBusinessID()
  const isOpen = Boolean(anchorEl)
  const { data: imports, isLoading, isError } = useCreativeImportRecent(isOpen)

  // Rows are transient — computed once per render, no ticking clock here.
  const now = Date.now()

  const openImport = (creativeImportID: number) => {
    onClose()
    history.push(`/${businessID}/creatives/import/${creativeImportID}`)
  }

  const rows = imports ?? []

  return (
    <Menu open={isOpen} anchorEl={anchorEl} onClose={onClose}>
      {isLoading && <MenuItem disabled>Loading…</MenuItem>}
      {!isLoading && isError && <MenuItem disabled>Could not load recent imports.</MenuItem>}
      {/* Also covers "every recent import expired" — that silence is intended. */}
      {!isLoading && !isError && rows.length === 0 && (
        <MenuItem disabled>No recent imports yet.</MenuItem>
      )}
      {rows.map((item: CreativeImportListItem) => {
        const chip = JOB_STATUS_CHIP[item.status]
        return (
          <MenuItem key={item.creativeImportID} onClick={() => openImport(item.creativeImportID)}>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Chip size="small" label={chip.label} color={chip.color} />
                <span>{format(new Date(item.createdAt), 'MMM d, h:mm a')}</span>
              </div>
              {item.archiveName && (
                <span className="text-xs truncate" title={item.archiveName}>
                  {item.archiveName}
                </span>
              )}
              {/* Expired unconfirmed imports are filtered out server-side; a
                    stale open menu is the only way one shows up here. */}
              {item.stagingExpiresAt && (
                <span className="text-xs">{expiryLabel(item.stagingExpiresAt, now)}</span>
              )}
            </div>
          </MenuItem>
        )
      })}
    </Menu>
  )
}
